import { useState, useEffect, FC, useMemo } from 'react';
import CustomDataGrid from 'components/CustomDataGrid';
import { CreditByStatus, CreditStatus, PaymentSortModel } from 'types';
import { useQuery } from '@apollo/client';
import { GET_CREDIT_BY_STATUS } from '../queries';
import { columnsPending } from '../Columns';
import { GridInitialStateCommunity } from '@mui/x-data-grid/models/gridStateCommunity';
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { OPTIONS_ROWS_PER_PAGE, ORDER, ORDER_BY_CREATED_AT } from '..';
import { DateRange } from 'react-day-picker';
import { useNavigate } from 'react-router-dom';
import { NetworkError } from 'components/CustomDataGrid/EmptyStates/NetworkError';
import { RequestError } from 'components/CustomDataGrid/EmptyStates/RequestError';
import { NoData } from 'components/CustomDataGrid/EmptyStates/NoData';
import {
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { StyledContainer } from 'screens/Login/styled';

type Props = {
  inputFilter: string;
  sortModel: string | null;
  dateRange: DateRange | undefined;
  handleFilterButton: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

const Pending: FC<Props> = ({ inputFilter, dateRange, handleFilterButton }) => {
  const navigate = useNavigate();
  const [allPayments, setAllPayments] = useState<CreditByStatus[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(OPTIONS_ROWS_PER_PAGE[0]);
  const initialStateGrid: GridInitialStateCommunity = {
    pagination: {
      paginationModel: {
        page: page,
        pageSize: rowsPerPage,
      },
    },
  };
  const sortModel: GridSortModel = [{ field: ORDER_BY_CREATED_AT, sort: ORDER }];
  const [isOnlineState, setIsOnlineState] = useState(navigator.onLine);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Fetch all payments at once
  const { loading, data, error, refetch } = useQuery(GET_CREDIT_BY_STATUS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      page: 0,
      limit: 10000,
      order: ORDER,
      orderBy: ORDER_BY_CREATED_AT,
      filters: {
        statuses: CreditStatus.PENDING,
        clientName: '',
        datesFilter: {
          dates: {
            startDate: dateRange?.from?.toISOString(),
            endDate: dateRange?.to?.toISOString(),
          },
          filterField: PaymentSortModel.CREATION,
        },
      },
    },
  });

  const handleChangePage = (objectPages: GridPaginationModel) => {
    setPage(objectPages.page);
    setRowsPerPage(objectPages.pageSize);
  };

  const handleErrorButton = async () => {
    setIsOnlineState(navigator.onLine);
    if (navigator.onLine) {
      refetch();
    }
  };

  useEffect(() => {
    if (!loading && !error && data) {
      const pendingCredits: CreditByStatus[] = data?.getCreditsByStatus ?? [];
      setAllPayments(pendingCredits);
    }
  }, [data, loading, error, dateRange]);

  useEffect(() => {
    setPage(0);
  }, [inputFilter]);

  const filteredPayments = useMemo(() => {
    if (!inputFilter) return allPayments;
    return allPayments.filter((payment) => {
      const fullName = payment.clientName.toLowerCase();
      const f = inputFilter.toLowerCase();
      return fullName.includes(f);
    });
  }, [allPayments, inputFilter]);

  const paginatedPayments = useMemo(
    () => filteredPayments.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredPayments, page, rowsPerPage],
  );

  const hasNoData = allPayments.length === 0 && !inputFilter && !dateRange && !loading;
  const noResultsWithFilter = inputFilter.length > 0 && filteredPayments.length === 0;

  if (!isOnlineState) return <NetworkError handleRetry={handleErrorButton} />;
  if (error) return <RequestError handleRetry={handleErrorButton} />;
  if (hasNoData) return <NoData message="No hay órdenes de pago pendientes" />;
  if (noResultsWithFilter)
    return (
      <NoData message="No se encontraron resultados para tu búsqueda. Recuerda que la búsqueda se realiza sobre todos los resultados." />
    );

  const handleClickChanguePayment = (id: string) => {
    setSelectedPaymentId(id);
    setOpenConfirm(true);
  };

  const handleConfirmChangePayment = () => {
    if (!selectedPaymentId) return;

    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');

    const raw = JSON.stringify({
      creditId: selectedPaymentId,
    });

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow' as RequestRedirect,
    };

    fetch(`${process.env.REACT_APP_PUBLIC_URL}api/v2/credit/changue_payment`, requestOptions)
      .then((response) => response.text())
      .then((result) => {
        console.log(result);
        setSnackbarMessage('Operación realizada con éxito');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      })
      .catch((error) => {
        console.error(error);
        setSnackbarMessage('Ocurrió un problema. Inténtalo de nuevo más tarde.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        refetch();
        setOpenConfirm(false);
      });
  };

  const handleCancelChangePayment = () => {
    setOpenConfirm(false);
  };

  return (
    <StyledContainer>
      <CustomDataGrid
        title="Listado de créditos pendientes"
        loading={loading}
        data={paginatedPayments}
        dataColumns={columnsPending({ handleClickChanguePayment })}
        initialState={initialStateGrid}
        pageSizeOptions={OPTIONS_ROWS_PER_PAGE}
        handleChangePage={handleChangePage}
        sort={sortModel}
        page={page}
        pageSize={rowsPerPage}
        totalRows={filteredPayments.length}
        filterButton={{ handleClick: handleFilterButton }}
      />
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {/* Modal de confirmación */}
      <Dialog open={openConfirm} onClose={handleCancelChangePayment}>
        <DialogTitle>Confirmación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea cambiar el crédito a Orden de Pago?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelChangePayment} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleConfirmChangePayment} color="primary">
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </StyledContainer>
  );
};

export default Pending;
